import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";

import { useDateRangeStore } from "./comps/date-range-picker/use-date-range-store";
import {
  getAllAttendancesOfEmployeeByDateQueryOptions,
  getAllEmployeesForSAQueryOptions,
} from "@/config/queries/superadmin-query-options";
import { convertUTCtoIST } from "@/utils";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { CheckIcon } from "lucide-react";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { DateRangePicker } from "./comps/date-range-picker/date-range-picker2";

ChartJS.register(ArcElement, Tooltip, Legend);

const AttendanceReport1OfEmployees = () => {
  const user = Cookies.get("key") as string;

  const { data, isPending, isFetching } = useQuery(
    getAllEmployeesForSAQueryOptions(user)
  );

  const [isEmpOpen, setIsEmpOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("");

  const employeesData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.employees === undefined) {
      return [];
    }

    const result = data?.employees?.map((emp) => {
      return {
        value: emp?._id,
        label: emp?.name,
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  const { dateRange } = useDateRangeStore();

  const startdate = useMemo(() => {
    return dateRange.from as Date;
  }, [dateRange]);

  const enddate = useMemo(() => {
    return dateRange.to as Date;
  }, [dateRange]);

  const {
    data: empAllAttendance,
    isPending: isAllAttendancePending,
    isFetching: isAllAttendanceFetching,
  } = useQuery(
    getAllAttendancesOfEmployeeByDateQueryOptions(
      user,
      convertUTCtoIST(new Date(startdate.setHours(0))),
      convertUTCtoIST(new Date(enddate.setHours(23))),
      selectedEmployee
    )
  );

  console.log(empAllAttendance);

  const chartData = useMemo(() => {
    const labels = [
      "Present",
      "Absent",
      "Half-day",
      "Paid Leave",
      "On Leave",
      // "Holiday",
      "Not Counting",
    ];

    let datasets = [
      {
        label: "count",
        data: Array(labels.length).fill(0),
        backgroundColor: [
          "rgba(255, 99, 132, 0.2)",
          "rgba(54, 162, 235, 0.2)",
          "rgba(255, 206, 86, 0.2)",
          "rgba(75, 192, 192, 0.2)",
          "rgba(153, 102, 255, 0.2)",
          "rgba(255, 159, 64, 0.2)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ],
        borderWidth: 1,
      },
    ];

    if (isAllAttendancePending) {
      return {
        labels,
        datasets,
      };
    }

    if (
      empAllAttendance === undefined ||
      empAllAttendance.attendance === undefined
    ) {
      return {
        labels,
        datasets,
      };
    }

    let newData = datasets[0]?.data as number[];
    const allAttendance = empAllAttendance?.attendance;

    for (const attendance of allAttendance) {
      const whichIndex = labels.findIndex((lab) => lab === attendance?.status);
      newData[whichIndex] = newData[whichIndex] + 1;
    }

    datasets[0].data = newData;

    return {
      labels,
      datasets,
    };
  }, [isAllAttendanceFetching, isAllAttendancePending, empAllAttendance]);


  return (
    <div className="p-5">
      <div className="">
        <h1 className="text-2xl font-semibold">Report 1</h1>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <Popover open={isEmpOpen} onOpenChange={setIsEmpOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-[200px] justify-between"
            >
              {selectedEmployee
                ? employeesData.find((emp) => emp.value === selectedEmployee)
                    ?.label
                : "Select employee..."}
              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0">
            <Command>
              <CommandInput placeholder="Search employee..." className="h-9" />
              <CommandEmpty>No employee found.</CommandEmpty>
              <CommandGroup>
                <CommandList>
                  {employeesData.map((emp) => (
                    <CommandItem
                      key={emp.value}
                      value={emp.label}
                      onSelect={() => {
                        setSelectedEmployee(emp.value);
                        setIsEmpOpen(false);
                      }}
                    >
                      {emp.label}
                      <CheckIcon
                        className={cn(
                          "ml-auto h-4 w-4",
                          selectedEmployee === emp.value
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandList>
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>

        <DateRangePicker />
      </div>

      <div className="flex items-center">
        <div className="w-[500px] mx-auto">
          <Pie data={chartData} />
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport1OfEmployees;
